import { Checkbox, Group, Modal, Progress, Select, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useFileManager } from 'api/file-manager/file-manager-context';
import UploadFiles from 'components/files/upload/UploadFiles';
import { IFileState } from 'components/files/upload/types';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import panic from 'errors/panic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { showNotification } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import * as XLSX from 'xlsx';
import P3Regular from 'components/typography/P3Regular';
import { isEvidenceFieldDeviceProperty } from 'components/forms/remote-source/config/device-properties';
import { XlsxConfig } from 'components/forms/remote-source/config/types';
import { XlsxImportDevicePayload } from 'api/actions/xlsx-import-device/xlsx-import-device-payload';
import { XlsxImportRevisionPlanPayload } from 'api/actions/xlsx-import-revision-plan/xlsx-import-revision-plan-payload';
import { XlsxImportDeviceFaultPayload } from 'api/actions/xlsx-import-device-fault/xlsx-import-device-fault-payload';
import { useApi } from 'api/api-context';
import { AxiosError } from 'axios';
import P2Medium from 'components/typography/P2Medium';
import { DEVICES_PAGE_PATH, DEVICE_REVISION_PLANS_PAGE_PATH, FAULTS_PAGE_PATH } from 'routes/paths';
import sleep from 'utils/sleep';
import Button from 'components/Button';

const ACCEPT_XLSX_TYPES = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx'];
type XlsxImportDeviceEvidenceFieldPayload = NonNullable<XlsxImportDevicePayload['evidenceFields']>[number];

export type ImportTarget = 'devices' | 'revisionPlans' | 'faults';

export interface ImportXlsxModalProps {
  opened: boolean;
  onClose: () => void;
  config: XlsxConfig;
  remoteSourceId: number;
}

/**
 * Modal for importing XLSX data for remote sources.
 */
export default function ImportXlsxModal({ opened, onClose, config, remoteSourceId }: ImportXlsxModalProps) {
  const { getAction } = useApi();
  const { readFile } = useFileManager();
  const [loading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [processed, setProcessed] = useState(0);
  const [total, setTotal] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [logs, setLogs] = useState<{ message: string }[]>([]);
  const [paused, setPaused] = useState(false);

  const pauseRef = useRef(false);
  const cancelRef = useRef(false);

  useEffect(() => {
    pauseRef.current = paused;
  }, [paused]);

  const form = useForm<{
    importTarget: ImportTarget;
    hasHeader: boolean;
    autoPauseOnError: boolean;
    files: IFileState[];
  }>({
    initialValues: { importTarget: 'devices', hasHeader: false, autoPauseOnError: true, files: [] },
    validate: {
      importTarget: createValidator([required]),
    },
  });

  const options = useMemo(
    () => [
      { value: 'devices', label: 'Zariadenia' },
      { value: 'revisionPlans', label: 'Termíny' },
      { value: 'faults', label: 'Nedostatky' },
    ],
    []
  );

  /**
   * Waits while the import is paused to allow resume without losing loop progress.
   */
  const waitWhilePaused = async () => {
    while (pauseRef.current && !cancelRef.current) {
      await sleep(150);
    }
  };

  /**
   * Converts the first sheet of the XLSX file into an array of objects keyed by column letters.
   */
  const extractSheetData = async (fileId: string, hasHeader: boolean) => {
    const blob = await readFile({ fileId });
    const arrayBuffer = await blob.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });

    if (workbook.SheetNames.length === 0) {
      throw new Error('Súbor neobsahuje žiadny hárok.');
    }

    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(firstSheet, {
      header: 'A',
      defval: '',
      range: hasHeader ? 1 : 0,
      UTC: true,
    });

    /**
     * Normalizes date strings from DD.MM.YYYY or MM/DD/YYYY to YYYY-MM-DD without creating Date objects.
     */
    const normalizeDate = (value: unknown) => {
      if (value instanceof Date) {
        const yyyy = value.getUTCFullYear();
        const mm = String(value.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(value.getUTCDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }

      if (typeof value !== 'string') {
        return value;
      }

      const trimmed = value.trim();
      const dotMatch = /^([0-9]{1,2})\.([0-9]{1,2})\.([0-9]{4})$/.exec(trimmed);
      if (dotMatch) {
        const [, dd, mm, yyyy] = dotMatch;
        const paddedDd = dd.padStart(2, '0');
        const paddedMm = mm.padStart(2, '0');
        return `${yyyy}-${paddedMm}-${paddedDd}`;
      }

      const slashMatch = /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{4})$/.exec(trimmed);
      if (slashMatch) {
        const [, mm, dd, yyyy] = slashMatch;
        const paddedDd = dd.padStart(2, '0');
        const paddedMm = mm.padStart(2, '0');
        return `${yyyy}-${paddedMm}-${paddedDd}`;
      }

      // ISO 8601 format
      const isoMatch = /^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{2}):([0-9]{2}):([0-9]{2})(\.[0-9]+)?Z$/.exec(trimmed);
      if (isoMatch) {
        const [, yyyy, mm, dd] = isoMatch;
        return `${yyyy}-${mm}-${dd}`;
      }

      return value;
    };

    return rows.map((row) =>
      Object.fromEntries(Object.entries(row).map(([key, value]) => [key, normalizeDate(value)]))
    );
  };

  /**
   * Imports devices based on the provided data rows.
   */
  const importDevices = async (data: Record<string, unknown>[], startingRow = 1, autoPauseOnError = false) => {
    const importDevice = getAction('XlsxImportDevice');

    const parsed = data.map((row, index) => {
      const device: XlsxImportDevicePayload & { row: number } = {
        row: index + startingRow,
        remoteSourceId,
        evidenceFields: [],
      };

      config.devices
        .filter(({ property, column }) => !!property && !!column)
        .forEach(({ column, property, evidenceFieldId }) => {
          const cellValue = String(row[column!] ?? '');

          if (isEvidenceFieldDeviceProperty(property)) {
            device.evidenceFields!.push({
              evidenceFieldId: Number(evidenceFieldId!),
              value: cellValue,
              insertOnly: property === 'evidenceFieldInsertOnly',
            } as XlsxImportDeviceEvidenceFieldPayload);
          } else if (property === 'manufactured') {
            device.manufactured = isNaN(Number(cellValue)) ? undefined : Number(cellValue);
          } else {
            (device as any)[property!] = cellValue;
          }
        });

      return device;
    });

    setTotal(parsed.length);
    setProcessed(0);
    setSuccessCount(0);
    setErrorCount(0);
    setCreatedCount(0);
    setLogs([]);

    let localSuccess = 0;
    let localError = 0;
    let localCreated = 0;

    for (const payload of parsed) {
      await waitWhilePaused();
      if (cancelRef.current) break;

      try {
        const { created } = await importDevice({ payload });
        setSuccessCount((value) => value + 1);
        localSuccess += 1;
        if (created) {
          setCreatedCount((value) => value + 1);
          localCreated += 1;
        }
      } catch (err) {
        const reason =
          err instanceof AxiosError
            ? err.response?.data?.error?.message || err.response?.data?.error?.error_message
            : 'Neznáma chyba';
        setErrorCount((value) => value + 1);
        localError += 1;
        setLogs((current) => [...current, { message: `❌ R${payload.row}: ${reason}` }]);

        if (autoPauseOnError) {
          setPaused(true);
          pauseRef.current = true;
        }
      } finally {
        if (!cancelRef.current) {
          setProcessed((value) => value + 1);
        }
      }
    }

    return { success: localSuccess, failed: localError, total: parsed.length, created: localCreated };
  };

  /**
   * Imports revision plans based on provided data rows.
   */
  const importRevisionPlans = async (data: Record<string, unknown>[], startingRow = 1, autoPauseOnError = false) => {
    const importRevisionPlan = getAction('XlsxImportRevisionPlan');

    const parsed = data.map((row, index) => {
      const revisionPlan: XlsxImportRevisionPlanPayload & { row: number } = {
        row: index + startingRow,
        remoteSourceId,
      };

      config.revisionPlans
        .filter(({ property, column }) => !!property && !!column)
        .forEach(({ column, property }) => {
          const cellValue = String(row[column!] ?? '');

          if (['deviceId', 'deviceTypeId', 'period'].includes(property!)) {
            const numeric = Number(cellValue);
            if (!Number.isNaN(numeric) && cellValue !== '') {
              (revisionPlan as any)[property!] = numeric;
            } else {
              (revisionPlan as any)[property!] = undefined;
            }
          } else if (property === 'neverExecuted') {
            revisionPlan.neverExecuted = cellValue.trim() !== '' && cellValue.trim() !== '0';
          } else {
            (revisionPlan as any)[property!] = cellValue;
          }
        });

      return revisionPlan;
    });

    setTotal(parsed.length);
    setProcessed(0);
    setSuccessCount(0);
    setErrorCount(0);
    setCreatedCount(0);
    setLogs([]);

    let localSuccess = 0;
    let localError = 0;
    let localCreated = 0;

    for (const payload of parsed) {
      await waitWhilePaused();
      if (cancelRef.current) break;

      try {
        const { created } = await importRevisionPlan({ payload });
        setSuccessCount((value) => value + 1);
        localSuccess += 1;
        if (created) {
          setCreatedCount((value) => value + 1);
          localCreated += 1;
        }
      } catch (err) {
        const reason =
          err instanceof AxiosError
            ? err.response?.data?.error?.message || err.response?.data?.error?.error_message
            : 'Neznáma chyba';
        setErrorCount((value) => value + 1);
        localError += 1;
        setLogs((current) => [...current, { message: `❌ R${payload.row}: ${reason}` }]);

        if (autoPauseOnError) {
          setPaused(true);
          pauseRef.current = true;
        }
      } finally {
        if (!cancelRef.current) {
          setProcessed((value) => value + 1);
        }
      }
    }

    return { success: localSuccess, failed: localError, total: parsed.length, created: localCreated };
  };

  /**
   * Imports device faults based on provided data rows.
   */
  const importDeviceFaults = async (data: Record<string, unknown>[], startingRow = 1, autoPauseOnError = false) => {
    const importDeviceFault = getAction('XlsxImportDeviceFault');

    const parsed = data.map((row, index) => {
      const fault: XlsxImportDeviceFaultPayload & { row: number } = {
        row: index + startingRow,
        remoteSourceId,
      };

      config.faults
        .filter(({ property, column }) => !!property && !!column)
        .forEach(({ column, property }) => {
          const cellValue = String(row[column!] ?? '');

          if (['deviceId', 'faultSeverityId'].includes(property!)) {
            const numeric = Number(cellValue);
            if (!Number.isNaN(numeric) && cellValue !== '') {
              (fault as any)[property!] = numeric;
            } else {
              (fault as any)[property!] = undefined;
            }
          } else {
            (fault as any)[property!] = cellValue;
          }
        });

      return fault;
    });

    setTotal(parsed.length);
    setProcessed(0);
    setSuccessCount(0);
    setErrorCount(0);
    setCreatedCount(0);
    setLogs([]);

    let localSuccess = 0;
    let localError = 0;
    let localCreated = 0;

    for (const payload of parsed) {
      await waitWhilePaused();
      if (cancelRef.current) break;

      try {
        const { created } = await importDeviceFault({ payload });
        setSuccessCount((value) => value + 1);
        localSuccess += 1;
        if (created) {
          setCreatedCount((value) => value + 1);
          localCreated += 1;
        }
      } catch (err) {
        const reason =
          err instanceof AxiosError
            ? err.response?.data?.error?.message || err.response?.data?.error?.error_message
            : 'Neznáma chyba';
        setErrorCount((value) => value + 1);
        localError += 1;
        setLogs((current) => [...current, { message: `❌ R${payload.row}: ${reason}` }]);

        if (autoPauseOnError) {
          setPaused(true);
          pauseRef.current = true;
        }
      } finally {
        if (!cancelRef.current) {
          setProcessed((value) => value + 1);
        }
      }
    }

    return { success: localSuccess, failed: localError, total: parsed.length, created: localCreated };
  };

  /**
   * Handles form submission.
   */
  const handleSubmit = async ({ importTarget, hasHeader, autoPauseOnError }: typeof form.values) => {
    try {
      const [file] = form.values.files;

      if (!file?.fileId || !file.finalized) {
        form.setFieldError('files', 'Nahrajte jeden XLSX súbor.');
        return;
      }

      cancelRef.current = false;

      startLoading();
      setPaused(false);
      pauseRef.current = false;

      const rows = await extractSheetData(file.fileId, hasHeader);

      let summary;

      if (importTarget === 'devices') {
        summary = await importDevices(rows, hasHeader ? 2 : 1, autoPauseOnError);
      } else if (importTarget === 'revisionPlans') {
        summary = await importRevisionPlans(rows, hasHeader ? 2 : 1, autoPauseOnError);
      } else if (importTarget === 'faults') {
        summary = await importDeviceFaults(rows, hasHeader ? 2 : 1, autoPauseOnError);
      }

      if (!cancelRef.current) {
        showNotification({
          title: 'Súbor bol spracovaný',
          message: summary
            ? `Import dokončený. Úspešné: ${summary.success}, neúspešné: ${summary.failed}, nové: ${summary.created}, spolu: ${summary.total}.`
            : 'Dáta boli načítané, pokračujte v importe podľa potreby.',
          color: SUCCESS_NOTIFICATION_COLOR,
        });
      }

      // handleClose();
    } catch (err) {
      panic(err as Error);
    } finally {
      stopLoading();
    }
  };

  /**
   * Resets modal state and closes it.
   */
  const handleClose = () => {
    cancelRef.current = true;
    form.reset();
    setProcessed(0);
    setTotal(0);
    setSuccessCount(0);
    setErrorCount(0);
    setLogs([]);
    setCreatedCount(0);
    setPaused(false);
    pauseRef.current = false;
    stopLoading();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Importovať dáta"
      centered
      size="lg"
      closeButtonProps={{ loading }}
      closeOnClickOutside={!loading && !form.isDirty()}
      closeOnEscape={!loading && !form.isDirty()}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing={12}>
          <Select
            data={options}
            label="Čo chcete importovať?"
            placeholder="Vyberte typ importu"
            readOnly={loading}
            {...form.getInputProps('importTarget')}
          />
          <Stack spacing={4}>
            <P3Regular color="dimmed">Podporovaný je jeden XLSX súbor. Spracovaný bude iba prvý hárok.</P3Regular>
            <UploadFiles
              accept={ACCEPT_XLSX_TYPES}
              maxFiles={1}
              onChange={(files = []) => form.setFieldValue('files', files)}
              error={form.errors.files as string | undefined}
              w="100%"
            />
            <Checkbox
              label="Súbor obsahuje hlavičku v prvom riadku"
              disabled={loading}
              {...form.getInputProps('hasHeader', { type: 'checkbox' })}
            />
            <Checkbox
              label="Pri chybe pozastaviť import"
              disabled={loading}
              {...form.getInputProps('autoPauseOnError', { type: 'checkbox' })}
            />
          </Stack>
          {total > 0 && (
            <Stack spacing={0}>
              <Group spacing={8} noWrap>
                <Progress w="100%" value={(processed / total) * 100} animate={loading} striped={loading} />
                <P3Regular color="gray.6">
                  {processed}/{total}
                </P3Regular>
              </Group>
              <Group spacing={16}>
                <P2Medium color="green.9">Úspešné: {successCount}</P2Medium>
                <P2Medium color="red.8">Neúspešné: {errorCount}</P2Medium>
                <P2Medium color="blue.8">Nové: {createdCount}</P2Medium>
              </Group>
              <Stack px={8} py={4} mt={8} bg="gray.0" spacing={4} pb={16} className="[&:empty]:hidden">
                {logs.map((log, idx) => (
                  <P3Regular key={`error-${idx}`} color="red.8">
                    {log.message}
                  </P3Regular>
                ))}
              </Stack>
            </Stack>
          )}
          <Group position="right" spacing={12} mt={16} noWrap>
            <Button variant="subtle-gray" onClick={handleClose} type="button" disabled={loading && !paused}>
              {loading ? 'Zrušiť' : 'Zatvoriť'}
            </Button>
            {total === 0 && !loading ? (
              <Button variant="primary" type="submit" loading={loading}>
                Importovať
              </Button>
            ) : loading ? (
              <Button
                variant={paused ? 'primary' : 'outline'}
                type="button"
                onClick={() => setPaused((value) => !value)}
              >
                {paused ? 'Pokračovať' : 'Pozastaviť'}
              </Button>
            ) : (
              <Button
                component="a"
                href={`${
                  form.values.importTarget === 'revisionPlans'
                    ? DEVICE_REVISION_PLANS_PAGE_PATH.original
                    : form.values.importTarget === 'faults'
                    ? FAULTS_PAGE_PATH.original
                    : DEVICES_PAGE_PATH.original
                }?remoteSourceId=${remoteSourceId}`}
                variant="primary"
                type="button"
              >
                {form.values.importTarget === 'revisionPlans'
                  ? 'Zobraziť termíny'
                  : form.values.importTarget === 'faults'
                  ? 'Zobraziť nedostatky'
                  : 'Zobraziť zariadenia'}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
